import { DataAction } from '../../../../enum/data-action.enum';
import { ExampleDetail } from '../../../models/example-detail.model';
import { Validators, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { ExampleService } from '../../../services/example.service';
import { Subject, takeUntil, switchMap, Observable, map, of, lastValueFrom } from 'rxjs';
import { ExampleData } from '../../../models/example-data.model';
import { Option } from '../../../../models/option.model';
import { Component } from '@angular/core';
import { SimpleDoubleDetailComponent } from '../../../../components/abstract/simple/simple-double-detail.component';
import { v4 as uuidv4 } from 'uuid';

@Component({
  selector: 'app-example-double-detail',
  templateUrl: './example-double-detail.component.html',
  styleUrls: ['./example-double-detail.component.scss']
})
export class ExampleDoubleDetailComponent extends SimpleDoubleDetailComponent {

  override componentClassName: string = 'ExampleDoubleDetailComponent';

  override masterData: ExampleDetail | undefined;

  // 下拉選單的 Options 資料
  exampleOptions: Option[] = [];

  // 動態下拉選單的 Options 資料
  dynamicOptions: Option[] = [];

  // 自動完成的 Suggestions 資料
  exampleDataSuggestions: ExampleData[] = [];
  // 自動完成欄位值變動的 Subject，用來避免前面一次查詢比較慢返回造成覆蓋資料
  private exampleDataSubject$ = new Subject<void>();

  // 自動完成 + 下拉的 Suggestions 資料
  allExampleData?: ExampleData[];
  // 自動完成 + 下拉的 Suggestions 資料
  allExampleDataSuggestions: ExampleData[] = [];
  // 自動完成 + 下拉欄位值變動的 Subject，用來避免前面一次查詢比較慢返回造成覆蓋資料
  private allExampleDataSubject$ = new Subject<void>();

  constructor(private exampleService: ExampleService) {
    super();
  }

  override initFormGroup(): void {
    console.log('ExampleDoubleDetailComponent initFormGroup');

    this.formGroup = this.formBuilder.nonNullable.group({
      id: '',
      uuid: '',
      detailInput: '',
      detailCalendar: '',
      detailDropdown: ['', Validators.required],
      detailAutoCompleteCode: '',
      detailInputNumber: null,
      detailCalendarRange: '',
      detailCustValidation: ['', this.custValidator()],
      detailDynamicDropdown: '',
      detailAutoCompleteDropdownCode: ''
    });

    // 設定欄位變更訂閱
    this.formControl('detailDropdown').valueChanges.pipe(
      takeUntil(this._destroying$)
    ).subscribe(value => {
      this.setDetailCalendarRangeDisable();
    });
  }

  override initFormControlDisabled(): void {
    this.setDetailCalendarRangeDisable();
  }

  /**
   * 下拉選單 = N，日期區間不能輸入。
   * 建議把這種欄位的控制單獨寫成一個 method，因為很多地方可能都會需要觸發，就不用重複寫。
   * 裡面取值都用 getRawValue() 來取。
   * 如果用 this.formGroup.value.xxxx 去取，在欄位的 valueChanges 的時候取自己欄位的值會是前一個狀態的值。
   *
   * 這個應該在欄位變更的時候呼叫，以及 initFormControlDisabled 的時候呼叫。
   */
  setDetailCalendarRangeDisable(): void {
    // 如果是在瀏覽狀態就不做欄位的控制
    if (this.masterInViewStatus()) {
      return;
    }

    if (this.formControl('detailDropdown').getRawValue() == 'N') {
      this.formControl('detailCalendarRange').disable();
    } else {
      this.formControl('detailCalendarRange').enable();
    }
  }

  override initStaticOptions(): void {
    console.log('ExampleDoubleDetailComponent initStaticOptions');
    // 下拉選單選項
    this.exampleOptions = [
      {
        id: 'option.yes',
        label: this.translateService.instant('option.yes'),
        value: 'Y'
      },
      {
        id: 'option.no',
        label: this.translateService.instant('option.no'),
        value: 'N'
      }
    ];
  }

  override async initFormData(): Promise<any> {
    this.masterData = await lastValueFrom(this.loadFormData());

    this.formGroup.patchValue({
      id: this.masterData?.id,
      uuid: this.masterData?.uuid,
      detailInput: this.masterData?.detailInput,
      detailCalendar: this.masterData?.detailCalendar,
      detailDropdown: this.masterData?.detailDropdown,
      detailInputNumber: this.masterData?.detailInputNumber,
      detailCustValidation: this.masterData?.detailCustValidation,
      detailDynamicDropdown: this.masterData?.detailDynamicDropdown
    });

    if (this.masterData.id == DataAction.NEW) {
      this.formGroup.patchValue({
        uuid: uuidv4()
      });
    }

    await this.setDynamicOptions();
    await Promise.all([this.patchAutoComplete(), this.patchAutoCompleteDropdown()]);
  }

  loadFormData(): Observable<ExampleDetail> {
    // 雙檔開起來的資料維護都用前面傳進來的資料，因為有可能還沒存到 DB 內，所以不能重取
    const data: ExampleDetail = this.dynamicDialogConfig.data;
    console.log('loadFormData = ' + JSON.stringify(data));
    return of(data);
  }

  /**
   * 建議這些要在程式一開始前就取回來的東西分不同 function 處理
   */
  async setDynamicOptions(): Promise<void> {
    console.log('setDynamicOptions start');
    this.dynamicOptions = await lastValueFrom(
      this.exampleService.queryAllExampleDataOptions().pipe(takeUntil(this._destroying$))
    );
    console.log('setDynamicOptions end');
  }

  async patchAutoComplete(): Promise<void> {
    console.log('patchAutoComplete start');
    const keyword = this.masterData?.detailAutoCompleteCode;
    if (keyword) {
      const data: ExampleData[] = await lastValueFrom(
        this.exampleService.queryExampleDatas(keyword).pipe(takeUntil(this._destroying$))
      );

      if (data.length > 0) {
        this.formGroup.patchValue({
          detailAutoCompleteCode: data.find(exampleData => exampleData.exampleDataCode == keyword)
        });
      } else {
        throw new Error('AutoComplete data ' + keyword + ' not found');
      }
    }
  }

  async patchAutoCompleteDropdown(): Promise<void> {
    console.log('patchAutoCompleteDropdown start');
    const keyword = this.masterData?.detailAutoCompleteDropdownCode;
    if (keyword) {
      const data: ExampleData[] = await lastValueFrom(
        this.exampleService.queryExampleDatas(keyword).pipe(takeUntil(this._destroying$))
      );

      if (data.length > 0) {
        this.formGroup.patchValue({
          detailAutoCompleteDropdownCode: data.find(exampleData => exampleData.exampleDataCode == keyword)
        });
      } else {
        throw new Error('AutoCompleteDropdown data ' + keyword + ' not found');
      }
    }
  }

  override afterFormDataLoadSuccess(): void {
    return;
  }

  override afterFormDataLoadFail(): void {
    return;
  }

  override afterLangChange(): void {
    this.languageService.processOptionTranslation(this.exampleOptions);
  }

  override async beforeSave(): Promise<boolean> {
    return true;
  }

  override async doSave(): Promise<boolean> {
    let submitData: ExampleDetail = new ExampleDetail();

    // 存檔後如果 id 是 new 表示新增的資料，把 id 清空，之後才不會被判斷成是新增未存檔的資料
    submitData.id = this.formGroup.getRawValue().id != DataAction.NEW ? this.formGroup.getRawValue().id : '';
    submitData.uuid = this.formGroup.getRawValue().uuid;
    submitData.detailInput = this.formGroup.getRawValue().detailInput;
    submitData.detailCalendar = this.formGroup.getRawValue().detailCalendar;
    submitData.detailDropdown = this.formGroup.getRawValue().detailDropdown;
    submitData.detailAutoCompleteCode = this.formGroup.getRawValue().detailAutoCompleteCode?.exampleDataCode;
    submitData.detailAutoCompleteName = this.formGroup.getRawValue().detailAutoCompleteCode?.exampleDataName;
    submitData.detailAutoCompleteNameTw = this.formGroup.getRawValue().detailAutoCompleteCode?.exampleDataNameTw;
    submitData.detailAutoCompleteNameCn = this.formGroup.getRawValue().detailAutoCompleteCode?.exampleDataNameCn;
    submitData.detailAutoCompleteNameUs = this.formGroup.getRawValue().detailAutoCompleteCode?.exampleDataNameUs;
    submitData.detailInputNumber = this.formGroup.getRawValue().detailInputNumber;
    submitData.detailCalendarRange = this.formGroup.getRawValue().detailCalendarRange;
    submitData.detailCustValidation = this.formGroup.getRawValue().detailCustValidation;
    submitData.detailDynamicDropdown = this.formGroup.getRawValue().detailDynamicDropdown;
    submitData.detailAutoCompleteDropdownCode = this.formGroup.getRawValue().detailAutoCompleteDropdownCode?.exampleDataCode;
    submitData.detailAutoCompleteDropdownName = this.formGroup.getRawValue().detailAutoCompleteDropdownCode?.exampleDataName;
    submitData.detailAutoCompleteDropdownNameTw = this.formGroup.getRawValue().detailAutoCompleteDropdownCode?.exampleDataNameTw;
    submitData.detailAutoCompleteDropdownNameCn = this.formGroup.getRawValue().detailAutoCompleteDropdownCode?.exampleDataNameCn;
    submitData.detailAutoCompleteDropdownNameUs = this.formGroup.getRawValue().detailAutoCompleteDropdownCode?.exampleDataNameUs;

    console.log('submitData = ' + JSON.stringify(submitData));

    this.masterData = submitData;

    return true;
  }

  custValidator(): ValidatorFn {
    return (formControl: AbstractControl): ValidationErrors | null => {
      if (!formControl.parent) {
        return null;
      }

      // 欄位要做什麼驗證
      const error = formControl.parent?.get('detailCustValidation')?.value == '1';

      // 如果有錯
      if (error) {
        // 回傳 ValidationErrors，duplicate 是 error 的 key
        return { duplicate: true };
      }
      // 驗證結果符合需求，回傳 null
      return null;
    }
  }

  searchExampleDataSuggestions(event: any) {
    // 這個訂閱不寫在最上面是希望讓自動完成欄位的程式集中在一起，比較好閱讀
    if (!this.exampleDataSubject$.observed) {
      this.exampleDataSubject$.pipe(
        switchMap(keyword => this.exampleService.queryExampleDatas(keyword!)),
        takeUntil(this._destroying$)
      ).subscribe(data => {
        this.exampleDataSuggestions = data;
        console.log('this.exampleDataSuggestions = ' + this.exampleDataSuggestions.length);
      });
    }

    this.exampleDataSubject$.next(event.query);
  }

  searchAllExampleDataSuggestions(event: any) {
    // 這個訂閱不寫在最上面是希望讓自動完成欄位的程式集中在一起，比較好閱讀
    if (!this.allExampleDataSubject$.observed) {
      this.allExampleDataSubject$.pipe(
        switchMap(keyword => {
          // 如果 allExampleData 已經有取過值了就用它，不然就呼叫 API 取
          let data: Observable<ExampleData[]> = this.allExampleData ? of(this.allExampleData) : this.exampleService.queryAllExampleDatas()
          return data.pipe(
            map(data => {
              console.log('searchAllExampleDataSuggestions = ' + keyword);
              // 把回傳所有的值塞到 allExampleData
              this.allExampleData = data;
              // 根據輸入的關鍵字過濾
              return data.filter(exampleData => exampleData.exampleDataName.indexOf(keyword!) >= 0)
            })
          );
        }),
        takeUntil(this._destroying$)
      ).subscribe(data => {
        this.allExampleDataSuggestions = data;
      });
    }

    this.allExampleDataSubject$.next(event.query);
  }
}
